import React, {
  memo,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import Collapse from '@material-ui/core/Collapse';

import {
  AppContext,
} from '../../../../../../../contexts';

import {
  FlexContainer,
  Select,
} from '../../../../../..';

import AddRemoveButton from '../../AddRemoveButton';

import FormularioIntervalo from './components/FormularioIntervalo';

import {
  CollapseLabel,
  IntervalContainer,
  SeletorDeCorEstilizado,
} from './styles';

/**
 * @typedef {object} RegraEscalaPorFiltroProps
 * @prop {SetState<RegraEscalaPorFiltro>} alterarEscalaPorFiltro Callback para alterar as regras
 * @prop {RegraEscalaPorFiltro[]} escalaPorFiltro Lista de regras definidas atualmente
 * @prop {RegraEscalaPorFiltro} regra Regra para esta componente
 * @prop {number} posicaoRegra Indíce da regra desta componente
 * @prop {boolean} permitirAplicar Booleano que define se a regra é aplicavel
 * @prop {SetState<boolean>} setPermitirAplicar setState de validação da regra
 */
/**
 * @param {RegraEscalaPorFiltroProps} props
 * @returns {JSX.Element} Componente que define uma regra de escala de cores
 */
function RegraEscalaPorFiltro(props) {
  const {
    escalaPorFiltro,
    permitirAplicar,
    regra,
    posicaoRegra,
    alterarEscalaPorFiltro,
    setPermitirAplicar,
  } = props;

  const { activeFilters } = useContext(AppContext);

  const { id, cor, item, intervalo, desabilitar } = regra;

  const adicionarRegra = useCallback(() => {
    alterarEscalaPorFiltro(atual => {
      const { length } = atual;
      const item = {
        id: atual[length - 1].id + 1,
        cor: '#0B8209',
        item: '',
      };
      return [...atual, item];
    });
  }, [alterarEscalaPorFiltro]);

  const removerRegra = useCallback(() => {
    alterarEscalaPorFiltro(old => old.filter(cs => cs.id !== id));
  }, [alterarEscalaPorFiltro, id]);

  const alterarRegra = useCallback((valor, chave) => {
    alterarEscalaPorFiltro(old => old.map((cs) => {
      if (cs.id === id) {
        cs[chave] = chave === 'intervalo' ? { ...cs[chave], ...valor } : valor;
      }
      return cs;
    }));
  }, [alterarEscalaPorFiltro, id]);

  const minimoInvalido = useMemo(() => (
    intervalo && (
      Number.isNaN(intervalo.de)
      || intervalo.de > intervalo.ate || intervalo.de < 0
    )
  ), [intervalo]);

  const maximoInvalido = useMemo(() => (
    intervalo && (
      Number.isNaN(intervalo.de) || intervalo.de > intervalo.ate
    )
  ), [intervalo]);

  /**
   * Opções de filtro para regra de cor
   * @type {SelectComponentOption[]}
   */
  const opcoesSelecao = useMemo(() => (
    activeFilters.length > 0
      ? activeFilters.map((item) => ({
        id: item.settings.title.join(', '),
        label: item.settings.title.join(', '),
      }))
      : []
  ), [activeFilters]);

  /**
   * Callback para adicionar um intervalo à regra.
   */
  const adicionarIntervalo = useCallback(() => {
    if (item && !desabilitar) {
      alterarEscalaPorFiltro(atual => (
        atual.map(regra => {
          if (regra.id !== id) return regra;
          return {
            ...regra,
            intervalo: {
              de: 0,
              ate: Number.MAX_VALUE,
            }
          };
        })
      ));
    }
  }, [item, id, desabilitar, alterarEscalaPorFiltro]);

  /**
   * Callback para remover o intervalo da regra.
   */
  const removerIntervalo = useCallback(() => {
    alterarEscalaPorFiltro(atual => (
      atual.map(regra => {
        if (regra.id !== id) return regra;
        delete regra.intervalo;
        return regra;
      })
    ));
  }, [id, alterarEscalaPorFiltro]);

  useEffect(() => {
    alterarRegra(
      item !== '' && !activeFilters.some(({ settings }) => item === settings.title.join(', ')),
      'desabilitar',
    );
  }, [item, activeFilters, alterarRegra]);

  useEffect(() => {
    setPermitirAplicar(
      intervalo
        ? item !== '' && (intervalo.de >= 0 && intervalo.de <= intervalo.ate)
        : item !== '' && !escalaPorFiltro.some(regra => regra.id !== id && regra.item === item && !regra.intervalo)
    );
  }, [escalaPorFiltro, id, item, intervalo, setPermitirAplicar]);

  return (
    <FlexContainer
      key={`regra-${id}-escala-por-filtro`}
      align="center"
      style={{ margin: '4px 0' }}
    >
      <SeletorDeCorEstilizado
        color={cor}
        setColor={(cor) => alterarRegra(cor, 'cor')}
        tamanho="1.25rem"
        popperTransform="translate(0.5rem, -50%)"
      />

      <FlexContainer
        flow="column"
        style={{ margin: '0 5px' }}
      >
        <Select
          disabled={desabilitar}
          onChange={({ target }) => alterarRegra(target.value, 'item')}
          label="Opções"
          value={item}
          style={{ margin: '0 3px', width: '100%' }}
          formControlStyle={{ minWidth: 210, width: '100%' }}
        >
          {
            desabilitar
              ? [{ id: item, label: item }]
              : opcoesSelecao.sort((a, b) => ((a.label > b.label) ? 1 : -1))
          }
        </Select>
        <CollapseLabel
          itemExists={!!item}
          intervalExists={!!intervalo}
          disable={desabilitar}
          variant="button"
          onClick={adicionarIntervalo}
        >
          DEFINIR QUANTIDADE
        </CollapseLabel>
        <IntervalContainer>
          <Collapse
            in={!desabilitar && item && intervalo}
            direction="up"
          >
            <FormularioIntervalo
              de={intervalo && intervalo.de}
              deInvalido={minimoInvalido}
              ate={intervalo && intervalo.ate}
              ateInvalido={maximoInvalido}
              alterarValorIntervalo={({ target }) => alterarRegra({ [target.name]: Number(target.value) }, 'intervalo')}
              removerIntervalo={removerIntervalo}
            />
          </Collapse>
        </IntervalContainer>
      </FlexContainer>
      <FlexContainer>
        {/* Remove */}
        {
          escalaPorFiltro.length > 1 && (
            <AddRemoveButton
              type="Remover"
              onClick={removerRegra}
              style={{ marginRight: 2 }}
            />
          )
        }

        {/* Add */}
        {
          posicaoRegra === escalaPorFiltro.length - 1 && (
            <AddRemoveButton
              type="Adder"
              onClick={adicionarRegra}
              disabled={!permitirAplicar}
              style={{ marginLeft: 2 }}
            />
          )
        }
      </FlexContainer>
    </FlexContainer>
  );
}

/**
 * Componente de definição da regra de coloração de Escala por Filtro.
 * Define uma cor para um filtro e pode definir um intervalo de quantidade a este filtro
 * @param alterarEscalaPorFiltro Callback para alterar as regras
 * @param escalaPorFiltro Lista de regras definidas atualmente
 * @param regra Regra para esta componente
 * @param posicaoRegra Indíce da regra desta componente
 * @param permitirAplicar Booleano que define se a regra é aplicavel
 * @param setPermitirAplicar setState de validação da regra
 */
export default memo(RegraEscalaPorFiltro);
